import { Component, Input, OnInit, ViewChild } from '@angular/core';
import {
  Account,
  CRM,
  CRMTypeId,
  Campaign,
  DashboardDateRanges,
  DashboardFilter,
  DashboardProductFilterRoute,
  DashboardSections,
  DashboardState,
  DashboardStageFilterRoute,
  abbrOfferStageLabel,
  MerchantProfileListItem,
  MerchantProfileStatus,
  Pager,
  PortalSessionTypes,
  SessionType,
  SessionTypeLabels,
  User,
} from '../../_models';
import { ReportTypes } from '../../reports/models/reports';

import {
  AccountService,
  AlertService,
  CRMService,
  CampaignService,
  DashboardService,
  MerchantProfileService,
  StorageService,
  UserService,
} from '../../_services';
import * as moment from 'moment';
import * as momentTz from 'moment-timezone';
import { DaterangepickerDirective } from 'ngx-daterangepicker-material';
import { Moment } from 'moment-timezone';
import { config } from '../../../config/config';
import { catchError, takeUntil } from 'rxjs/operators';
import { Subject, forkJoin, of } from 'rxjs';

@Component({
  selector: 'dashboard-filter-bar',
  templateUrl: './dashboard-filter-bar.component.html',
  styleUrls: ['./dashboard-filter-bar.component.scss'],
})
export class DashboardFilterBarComponent implements OnInit {
  @ViewChild('datePickerInput', { read: DaterangepickerDirective, static: false })
  pickerDirective: DaterangepickerDirective;
  @ViewChild('comparePickerInput', { read: DaterangepickerDirective, static: false })
  comparePickerDirective: DaterangepickerDirective;
  @Input() selectedSection: DashboardSections;
  productOptions: { product_id: number; product_name: string }[] = [];
  stageOptions: { value: number; label: string }[] = [];
  portalOptions: { key: string; label: string; types: number[] }[] = [
    { key: 'all', label: 'All Portals', types: PortalSessionTypes },
    ...PortalSessionTypes.map((type: SessionType) => ({ key: String(type), label: SessionTypeLabels[type], types: [type] })),
  ];
  portalKey = 'all';
  merchantProfiles: MerchantProfileListItem[] = [];
  selectedFilters: DashboardFilter = null;
  pendingFilters: DashboardFilter = null;
  currentUser: User;
  accounts: Account[] = [];
  crms: CRM[] = [];
  campaigns: Campaign[] = [];
  public updateSessionFilterFlag: boolean = true;

  /* Misc */
  protected dashboardServicesMap = {};
  dropdownsLoading = false;
  excludePrefulfillmentCancels: boolean = false;
  excludeAbandons: boolean = false;
  public state: DashboardState = {};
  protected _destroy$: Subject<boolean> = new Subject<boolean>();

  /* Dates */
  date: { startDate: Moment; endDate: Moment };
  compareDate: { startDate: Moment; endDate: Moment };
  maxDate: Moment;
  timezones: string[] = [];
  public ranges = DashboardDateRanges;

  constructor(
    private storageService: StorageService,
    private userService: UserService,
    protected dashboardService: DashboardService,
    protected accountService: AccountService,
    protected campaignService: CampaignService,
    protected crmService: CRMService,
    protected merchantProfileService: MerchantProfileService,
    protected alertService: AlertService
  ) {}

  ngOnInit() {
    this.userService.getCurrent().subscribe((user: User) => {
      this.currentUser = user;
    });
    this.timezones = momentTz.tz.names();
    this.getDashboardData();
    this.getAccountsList();
    this.getCRMList();
    this.initializeDate();
  }

  getDashboardData() {
    this.dashboardService.filters$.pipe(takeUntil(this._destroy$)).subscribe((change) => {
      let account_id = null;
      const prevSection = this.selectedFilters ? this.selectedFilters.section : null;

      if (this.selectedFilters && this.selectedFilters.account_id) {
        account_id = this.selectedFilters.account_id;
      }

      this.selectedFilters = change;
      this.pendingFilters = { ...change };
      this.portalKey = this.getPortalKey(change.portal_session_types);

      const accountChanged = account_id !== change.account_id;
      const sectionChanged = change.section !== prevSection;

      if (accountChanged) {
        this.updateCampaigns();
      }

      if (change.section === DashboardSections.Resolve && (accountChanged || sectionChanged)) {
        this.loadMerchantProfiles();
      }

      const loadsFilterOptionsOnSection =
        change.section === DashboardSections.Subscription ||
        change.section === DashboardSections.Order ||
        change.section === DashboardSections.Overview ||
        change.section === DashboardSections.Return;
      if (loadsFilterOptionsOnSection && sectionChanged) {
        this.loadProductOptions(change.section);
        this.loadStageOptions(change.section);
      }
    });
  }

  loadMerchantProfiles() {
    this.merchantProfileService
      .list()
      .pipe(
        takeUntil(this._destroy$),
        catchError((error) => {
          if (this.currentUser && this.currentUser.is_staff) {
            this.alertService.error(error.message);
          }
          return of(null);
        })
      )
      .subscribe((data: MerchantProfileListItem[]) => {
        let profiles: MerchantProfileListItem[] = [];
        if (Array.isArray(data)) {
          profiles = data.filter(
            (profile: MerchantProfileListItem) =>
              profile.status !== MerchantProfileStatus.Archived
          );
        }
        this.merchantProfiles = profiles;
      });
  }

  loadStageOptions(section: DashboardSections) {
    const routes =
      section === DashboardSections.Overview
        ? Object.values(DashboardStageFilterRoute)
        : [DashboardStageFilterRoute[section]];

    forkJoin(
      routes.map((r) =>
        this.dashboardService.getStageFilterOptions(r.route, r.reportType).pipe(
          catchError((error) => {
            this.alertService.error(error.message);
            return of(null);
          })
        )
      )
    ).subscribe((results: any[]) => {
      const codes = new Set<number>();
      results.forEach((data) => {
        if (data && Array.isArray(data.values)) {
          data.values.forEach((v: any) => codes.add(Number(v)));
        }
      });
      this.stageOptions = Array.from(codes)
        .filter((code) => abbrOfferStageLabel[code] !== undefined)
        .map((code) => ({ value: code, label: abbrOfferStageLabel[code] }));
    });
  }

  loadProductOptions(section: DashboardSections) {
    let route = 'order_dashboard';
    let reportType = ReportTypes.ORDER_RETENTION;
    const sectionRoute = DashboardProductFilterRoute[section];
    if (sectionRoute) {
      route = sectionRoute.route;
      reportType = sectionRoute.reportType;
    }
    this.dashboardService.getProductFilterOptions(route, reportType).subscribe(
      (data: any) => {
        if (data && data.results && Array.isArray(data.results)) {
          this.productOptions = data.results.map((p: any) => ({
            product_id: p.product_id,
            product_name: p.product_name,
          }));
        } else {
          this.productOptions = [];
        }
      },
      (error) => {
        this.productOptions = [];
        this.alertService.error(error.message);
      }
    );
  }

  updateCampaigns() {
    if (this.selectedFilters.account_id) {
      this.campaignService
        .list({ is_hybrid: false, account_id: this.selectedFilters.account_id })
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          (result: Pager) => {
            this.campaigns = result.results;
            if (this.dropdownsLoading) {
              this.dropdownsLoading = false;
            }
          },
          (error) => {
            this.alertService.error(error.message);
          }
        );
    }
  }

  getAccountsList() {
    this.accountService
      .list({ page: 1, page_size: config.maxPageSize })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          this.accounts = data.results;
        },
        (error) => {
          this.alertService.error(error.message);
        }
      );
  }

  getCRMList() {
    this.crmService
      .list({
        page: 1,
        page_size: config.maxPageSize,
        'type!': CRMTypeId.Test,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data: Pager) => {
          this.crms = data.results;
        },
        (error) => {
          this.alertService.error(error.message);
        }
      );
  }

  onAccountChange(event) {
    this.dropdownsLoading = true;
    this.dashboardService.setFilter({
      ...this.selectedFilters,
      account_id: event.target.value,
      campaign_id: null,
      merchant_profile_id: null,
    });
  }

  onCampaignChange(event) {
    this.dashboardService.setFilter({
      ...this.selectedFilters,
      campaign_id: event.target.value,
    });
    this.campaigns.forEach((campaign: Campaign) => {
      if (campaign.id.toString() === event.target.value.toString()) {
        this.state.campaign = campaign;
      }
    });
  }

  onCRMChange(event) {
    this.dashboardService.setFilter({
      ...this.selectedFilters,
      crm_id: event.target.value,
    });

    this.state.crm_id = event.target.value;
  }

  onTimezoneChange(event) {
    this.storageService.set('timezone', event.target.value);
    this.dashboardService.setFilter({
      ...this.selectedFilters,
      timezone: event.target.value,
    });
  }

  openDatepicker() {
    if (this.comparePickerDirective) {
      this.comparePickerDirective.hide();
    }
    this.pickerDirective.open();
  }

  openCompareDatepicker() {
    if (this.pickerDirective) {
      this.pickerDirective.hide();
    }
    this.comparePickerDirective.open();
  }

  onCompareDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    if (dateRange.startDate && dateRange.endDate) {
      const compareFilters = {
        compare_start_date: dateRange.startDate.format('YYYY-MM-DD HH:mm:ss'),
        compare_end_date: dateRange.endDate.format('YYYY-MM-DD HH:mm:ss'),
      };
      this.pendingFilters = { ...this.pendingFilters, ...compareFilters };
      this.onApply();
    }
  };

  initializeDate() {
    this.selectedFilters = this.dashboardService.getFilter();
    this.date = {
      startDate: moment(this.selectedFilters.start_date),
      endDate: moment(this.selectedFilters.end_date),
    };
    this.maxDate = this.date.endDate;
    if (this.selectedFilters.compare_start_date && this.selectedFilters.compare_end_date) {
      this.compareDate = {
        startDate: moment(this.selectedFilters.compare_start_date),
        endDate: moment(this.selectedFilters.compare_end_date),
      };
    }
  }

  onDateChange = (dateRange: { startDate: Moment; endDate: Moment }) => {
    if (dateRange.startDate && dateRange.endDate) {
      const dateFilters = {
        start_date: dateRange.startDate.format('YYYY-MM-DD HH:mm:ss'),
        end_date: dateRange.endDate.format('YYYY-MM-DD HH:mm:ss'),
      };
      if (this.isFetchOnApply) {
        this.pendingFilters = { ...this.pendingFilters, ...dateFilters };
        this.onApply();
      } else {
        this.dashboardService.setFilter({ ...this.selectedFilters, ...dateFilters });
      }
    }
  };

  getPendingValue(key: string): string {
    let value = '';
    if (this.pendingFilters && this.pendingFilters[key]) {
      value = this.pendingFilters[key];
    }
    return value;
  }

  onProductChange(event) {
    const value = event.target.value ? event.target.value : null;
    const selected = value ? this.productOptions.find((p) => String(p.product_id) === value) : null;
    this.pendingFilters = {
      ...this.pendingFilters,
      product_name: selected ? selected.product_name : null,
      product_id: selected ? selected.product_id : null,
    };
  }

  onSubscriptionCampaignChange(event) {
    this.pendingFilters = { ...this.pendingFilters, campaign_id: event.target.value };
  }

  onMerchantProfileChange(event) {
    const value = event.target.value ? event.target.value : null;
    this.pendingFilters = { ...this.pendingFilters, merchant_profile_id: value };
  }

  onOrdersStageChange(event) {
    const value = event.target.value ? Number(event.target.value) : null;
    this.pendingFilters = { ...this.pendingFilters, offer_intent: value };
  }

  getPortalKey(types: number[]): string {
    let key = 'all';
    if (types && types.length === 1) {
      key = String(types[0]);
    }
    return key;
  }

  onPortalChange(event) {
    const option = this.portalOptions.find((o) => o.key === event.target.value);
    this.portalKey = option.key;
    this.pendingFilters = { ...this.pendingFilters, portal_session_types: option.types };
  }

  onExcludePrefulfillmentCancelsChange(value: boolean) {
    this.excludePrefulfillmentCancels = value;
    this.pendingFilters = { ...this.pendingFilters, exclude_prefulfillment_cancels: value };
  }

  onExcludeAbandonsChange(value: boolean) {
    this.excludeAbandons = value;
    this.pendingFilters = { ...this.pendingFilters, exclude_abandons: value };
  }

  onApply() {
    if (this.pendingFilters) {
      this.dashboardService.setFilter(this.pendingFilters);
    }
  }

  get isOverviewSection() {
    return this.selectedSection === DashboardSections.Overview;
  }

  get isRetentionSection() {
    return this.selectedSection === DashboardSections.Retention;
  }

  get isMarketplaceSection() {
    return this.selectedSection === DashboardSections.Marketplace;
  }

  get isPhoneSection() {
    return this.selectedSection === DashboardSections.Phone;
  }

  get isSubscriptionSection() {
    return this.selectedSection === DashboardSections.Subscription;
  }

  get isOrdersSection() {
    return this.selectedSection === DashboardSections.Order;
  }

  get isReturnsSection() {
    return this.selectedSection === DashboardSections.Return;
  }

  get isResolveSection() {
    return this.selectedSection === DashboardSections.Resolve;
  }

  get isFetchOnApply() {
    return (
      this.isOverviewSection ||
      this.isSubscriptionSection ||
      this.isOrdersSection ||
      this.isReturnsSection ||
      this.isResolveSection
    );
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }
}
